//third party
import { useEffect, useState, useRef, useMemo } from "react";
import { useAtom, getDefaultStore, useSetAtom } from "jotai";
import { Navigate, useLocation, useNavigate, useParams, useLoaderData } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Row, Col, Card, Modal, Form } from "react-bootstrap";
import { DateTimeFormatter, Instant, LocalDateTime, LocalTime, ZoneId  } from "@js-joda/core";
import { CheckCircleFill, GeoAltFill, ArrowLeft } from "react-bootstrap-icons";

//stripe
import { Elements } from "@stripe/react-stripe-js";
import { StripeElementsOptions } from "@stripe/stripe-js";

//spotsync
import { authAtom, hourlyCheckoutAtom, redirectAtom, vehicleFromExtendQuery } from "./Data/Atoms";
import { push                           } from "./Data/Array";
import { Button                         } from "./Controls/Button";
import { ReservationDetailsHourly       } from "./Controls/ReservationDetailsHourly";
import { CardHeader                     } from "./Controls/CardHeader";
import { CheckoutIndicator              } from "./Controls/CheckoutIndicator";
import { AddVehicleModal                } from "./Controls/AddVehicleModal";
import { VehicleCard                    } from "./Controls/VehicleCard";
import { Content                        } from "./Controls/Content";
import { ChooseVehicleModal             } from "./Controls/ChooseVehicleModal";
import { ReserveInitResp, Vehicle, Facility } from "./Data/ApiTypes";
import { processResult                  } from "./Data/Result";
import { stripePromise                  } from "./Data/ApiStripe";
import { Api                            } from "./Data/Api";
import { ReserveInitReqApi              } from "./Data/ApiTransport";
import { parseReserveInitResp           } from "./Data/ApiParse";
import { OptInFeesSelection } from "./Controls/OptInFeesSelector";
import { StripeHourlyCheckoutForm       } from "./StripeHourlyCheckoutForm";
import { appearanceOptions, stripeFonts } from "./Controls/StripeAppearance";
import { Hyperlink } from "./Controls/Hyperlink";
import { isFeatureEnabled, FEATURES } from './Data/Features';
import { createApiService } from "./shared/apiConfig";
import { ParkingValidationRequestApi, ServerRequestsParkingValidationByIdData } from "./shared/api/axios-client";
import { toast } from "react-toastify";

enum CheckoutStep {
    Initial,
    Account,
    Reservation,
    Payment
}

interface State<TState, TContext> {
    name: string;
    cond: ( ctx: TContext ) => boolean;
    entr: ( ctx: TContext ) => TContext;
    exit: ( ctx: TContext ) => boolean;
}

export interface EmailForm {
    emailAddress: string;
}

export function CheckoutPageHourly() {
    const loc = useLocation();
    const nav = useNavigate();
    const { rateProgramId: rateProgramIdParam } = useParams();
    const { validationRequestId } = useParams();
    const [ auth,               setAuth               ] = useAtom( authAtom );
    const [ redirect,           setRedirect           ] = useAtom( redirectAtom );
    const [ checkoutUnd,        setCheckout           ] = useAtom( hourlyCheckoutAtom );
    const [ step,               setStep               ] = useState( CheckoutStep.Initial );
    const [ myVehicles,         setMyVehicles         ] = useState<Vehicle[]>( [] );
    const [ showChooseVehicle,  setShowChooseVehicle  ] = useState( false );
    const [ showAddVehicle,     setShowAddVehicle     ] = useState( false );
    const [ email,              setEmail              ] = useState<EmailForm>( { emailAddress: auth.isLoggedIn ? auth.account!.emailAddress : "" } );
    const [ reserveRequestResp, setReserveRequestResp ] = useState<ReserveInitResp>();
    const [ paymentVis,         setPaymentVis         ] = useState<"hidden" | "visible">( "hidden" );
    const [ rateProgramId,      setRateProgramId      ] = useState<number | null>(null);
    const [ showEventModal, setShowEventModal ] = useState(false);
    const [ eventDetails, setEventDetails ] = useState<any>(null);
    const [ requireLicensePlate, setRequireLicensePlate ] = useState(false);
    const [ rateAvailable, setRateAvailable ] = useState(false);
    const [ isInitializing, setIsInitializing ] = useState(true);
    const [ validationRequestData, setValidationRequestData ] = useState<ServerRequestsParkingValidationByIdData | undefined>(undefined);
    const [ selectedOptInFees, setSelectedOptInFees ] = useState<number[]>([]);
    
    const { register, handleSubmit: handleEmailSubmit, getValues, setFocus } = useForm<EmailForm>({ 
        defaultValues: { emailAddress: "" } 
    });

    const handleModalClose = () => {
        setShowEventModal(false);
        setTimeout(() => {
            setFocus("emailAddress", { shouldSelect: true });
        }, 300);
    };

    const handleRateAvailable = (isAvailable: boolean) => {
        setRateAvailable(isAvailable);
        setIsInitializing(false);
    };

    const handleOptInFeesChange = (selection: OptInFeesSelection) => {
        // Convert selection object to array of selected fee IDs
        const selectedIds = Object.keys(selection)
            .filter(id => selection[parseInt(id)])
            .map(id => parseInt(id));
        setSelectedOptInFees(selectedIds);
    };

    // facility will be passed for an event
    const facility = useLoaderData() as Facility;  
    
    // Handle rateProgramId parameter
    useEffect(() => {
        if (rateProgramIdParam) {
            const parsedId = parseInt(rateProgramIdParam);
            if (!isNaN(parsedId)) {
                const currentRateProgram = facility.hourlyRates.find(x => x.rateProgramId == parsedId);
                if (!currentRateProgram?.isActive)
                {
                    nav("/");
                    return;
                }
                setRateProgramId(parsedId);
                const now = LocalDateTime.now().withNano(0);
                // Set to 11:59 PM of the current day. This may need to be adjusted for events in the future
                const endTime = now.toLocalDate().atTime(LocalTime.of(23, 59));
              
                setCheckout({
                    ...checkout,
                    facility: facility,
                    start: now,
                    end: endTime,
                    vehicles: []
                });
                // Set event details and show modal
                setEventDetails({
                    facility: facility,
                    rateProgram: currentRateProgram
                });
                setShowEventModal(true);
            }         
        }
    }, [rateProgramIdParam]); 

    useEffect(() => {
        if (!validationRequestId) return;

        const loadValidationRequest = async () => {
            try {
                const validationApi = createApiService(ParkingValidationRequestApi);
                const { data } = await validationApi.getApprovedParkingValidationRequestById({
                    parkingValidationRequestId: validationRequestId
                });

                if (data.error) return nav("/?error=inviteExpired");

                if (!data?.startDateTime || !data?.endDateTime) return;

                setValidationRequestData(data)
                setRateProgramId(data.rateProgramId!);

                const startInstant = Instant.parse(data.startDateTime! as any);
                const start = startInstant.atZone(ZoneId.systemDefault()).toLocalDateTime();

                const endInstant = Instant.parse(data.endDateTime! as any);
                const end = endInstant.atZone(ZoneId.systemDefault()).toLocalDateTime();

                setCheckout(prev => ({
                    ...prev,
                    facility: facility,
                    start,
                    end,
                    vehicles: []
                }));
            } catch {
                return nav("/?error=inviteExpired");
            }
        };

        loadValidationRequest();
    }, [validationRequestId, facility]);


    const checkout = checkoutUnd!;
   
    useEffect( () => {
        if( auth.isLoggedIn ) {
            setEmail( { emailAddress: auth.account!.emailAddress } );
            // Clear stored guest email when user logs in
            localStorage.removeItem('checkoutGuestEmail');
            setStep( CheckoutStep.Reservation );
            return;
        }

        // Check for stored guest email in localStorage
        const storedEmail = localStorage.getItem('checkoutGuestEmail');
        if (storedEmail && storedEmail.trim() !== '') {
            setEmail( { emailAddress: storedEmail } );
            setStep( CheckoutStep.Reservation );
            return;
        }

        setEmail( { emailAddress: "" } );
        setStep( CheckoutStep.Account );           
    }, [auth.isLoggedIn] );

    useEffect( () => {
        if( auth.isLoggedIn ) {
            Api.vehicleList().then(
                res => processResult( res,
                    val => setMyVehicles( val ),
                    err => console.log( `Didn't retrieve vehicles because: ${err}`) ) ); //don't do anything if they didn't load
        }
    }, [] );

    useEffect(() => {
        if (!checkoutUnd) {
            return;
        }
        const normalizeParam = (value: string | null) => (value ?? "").replace(/\+/g, " ").trim();
        const params = new URLSearchParams(loc.search);
        const licensePlate = normalizeParam(params.get("licensePlate"));
        const licenseState = normalizeParam(params.get("licenseState"));
        const startTime = normalizeParam(params.get("startTime"));
        const duration = normalizeParam(params.get("duration"));
        if (!startTime) {
            return;
        }
        const shouldBuildVehicle = !!licensePlate && !!licenseState;
        const sessionAlreadyApplied =
            checkoutUnd.extendSession?.startTime === startTime &&
            (checkoutUnd.extendSession?.licensePlate ?? "") === licensePlate &&
            (checkoutUnd.extendSession?.licenseState ?? "") === licenseState &&
            (checkoutUnd.extendSession?.duration ?? "") === duration;
        if (sessionAlreadyApplied && (!shouldBuildVehicle || checkoutUnd.vehicles.length > 0)) {
            return;
        }

        const vehicles = shouldBuildVehicle
            ? [vehicleFromExtendQuery(licensePlate, licenseState)]
            : checkoutUnd.vehicles;
        setCheckout({
            ...checkoutUnd,
            vehicles,
            extendSession: {
                licensePlate,
                licenseState,
                startTime,
                duration
            }
        });
    }, [checkoutUnd, loc.search, setCheckout]);

    useEffect( () => {
        if (checkout && checkout.facility){
            setRequireLicensePlate(isFeatureEnabled(checkout.facility, FEATURES.REQUIRE_LICENSE_PLATE));
        }
        if( step === CheckoutStep.Payment ) {          
            const reserveReq: ReserveInitReqApi = {
                facilityId: checkout.facility.facilityId,
                start: checkout.start.format(DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm:ss")),
                end: checkout.end.format(DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm:ss")),
                guestEmail: !auth.isLoggedIn ? email.emailAddress : null,
                rateProgramId: rateProgramId,
                vehicle: checkout.vehicles[0],
                spaces: validationRequestId ? validationRequestData!.numberOfSpaces! : null,
                selectedOptInFees: selectedOptInFees.length > 0 ? selectedOptInFees : undefined,
                validationRequestId: validationRequestId
            };
            Api.reservationInit( reserveReq ).then(
                res => processResult( res,
                    val => {
                        setReserveRequestResp( parseReserveInitResp( val ) );
                        setPaymentVis( "visible" );
                    },
                    err => console.error( err ) ) );
        }
    }, [step] );

    // Only create options when we have a valid clientSecret
    const options: StripeElementsOptions | undefined = useMemo(() => {
        if (!reserveRequestResp?.paymentIntentClientSecret) {
            return undefined;
        }

        const baseOptions: StripeElementsOptions = {
            clientSecret: reserveRequestResp.paymentIntentClientSecret,
            appearance: appearanceOptions,
            fonts: stripeFonts
        };

        // Guest users should omit this property since no customer has been created in Stripe
        if (reserveRequestResp.customerSessionClientSecret) {
            baseOptions.customerSessionClientSecret = reserveRequestResp.customerSessionClientSecret;
        }

        return baseOptions;
    }, [reserveRequestResp]);

    function submitEmailAsGuest( email: EmailForm ) {
        setEmail( email );
        // Store email in localStorage for persistence across navigation
        localStorage.setItem('checkoutGuestEmail', email.emailAddress);
        setStep( CheckoutStep.Reservation );
    }

    const addVehicle  = ( v: Vehicle    ) => setCheckout( { ...checkout, vehicles: push( checkout.vehicles, v ) } );
    const setVehicles = ( vs: Vehicle[] ) => setCheckout( { ...checkout, vehicles: vs } );

    function handleLoginClick(e: React.MouseEvent) {
        e.preventDefault();
        const url = new URL(window.location.href);
        const store = getDefaultStore();
        store.set(redirectAtom, `${url.pathname}${url.search}`);
        nav("/login");
    }

    if( !checkout && !rateProgramIdParam && !validationRequestId) {
        return <Navigate to="/results" />;
    }

    return <Content fluid>
        <Modal show={showEventModal} onHide={handleModalClose} centered>
            <Modal.Header closeButton>
                <Modal.Title> 
                    <CheckCircleFill className="flex-shrink-0" style={{ fontSize: "1.5rem", color:"#FF4DB8", marginTop:"-6px" }} />     
                     &nbsp; Event Parking Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>      
                {eventDetails && <>          
                    <div className="mb-2 d-flex gap-3 align-items-start">
                        
                        <p><span className="fw-bold">Welcome to {eventDetails.facility.name}! </span><span className="text-secondary fs-7">Click continue to proceed with your parking session.</span></p>                                                
                    </div>
                    
                    <div className="fs-4 fw-bold mb-2"></div>
                    <div className="text-secondary mb-3 d-flex gap-2 align-items-start">
                        <GeoAltFill className="flex-shrink-0" style={{ fontSize: "1.2rem", marginTop: "0.2rem", color: "#6c757d" }} />
                        <div>
                            {eventDetails.facility.address.addressLine1}
                            {eventDetails.facility.address.addressLine2 && <>, {eventDetails.facility.address.addressLine2}</>}
                            <br />
                            {eventDetails.facility.address.city}, {eventDetails.facility.address.state} {eventDetails.facility.address.zipCode}
                        </div>
                    </div>
                </>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => nav('/')}>Cancel</Button>
                <Button className="btn-main" onClick={handleModalClose}>Continue</Button>
            </Modal.Footer>
        </Modal>

        <Row className="justify-content-center">
            <Col md="6">
                <AddVehicleModal show={showAddVehicle}
                                 setShow={setShowAddVehicle}
                                 onSubmit={addVehicle}
                                 isCheckout={true} />

                <ChooseVehicleModal vehicles={myVehicles}
                                    show={showChooseVehicle}
                                    setShow={setShowChooseVehicle}
                                    onSubmit={addVehicle} 
                                    onClose={() => setShowChooseVehicle(false)}/>

                <Card className="mb-2">
                    <CardHeader>
                        <CheckoutIndicator step={1} success={step > CheckoutStep.Account} /> Account Information
                    </CardHeader>
                    <Card.Body>
                        {email.emailAddress === "" && (
                            <div className="d-flex flex-column gap-2">
                                <div className="d-flex flex-column align-items-left gap-2">
                                    <Button style={{maxWidth: "100px"}} className="w-30" onClick={handleLoginClick}>Log in</Button>
                                    <div className="text-muted">OR</div>
                                    <div>Checkout as a guest by typing your email:</div>
                                </div>
                                <form onSubmit={handleEmailSubmit(submitEmailAsGuest)} className="d-flex gap-2">
                                    <input
                                        type="email"
                                        className="form-control"
                                        placeholder="ex: parker@email.com"
                                        {...register("emailAddress", { required: true })}
                                />
                                    <Button className="btn-main" type="submit" style={{ whiteSpace: "nowrap" }}>Continue as Guest</Button>
                                </form> 
                            </div>
                        )}
                        {email.emailAddress !== "" && (
                            <div className="d-flex justify-content-between align-items-center">
                                <span>Checking out as {email.emailAddress}</span>
                                {!auth.isLoggedIn && (
                                    <Button variant="outline-secondary" size="sm" onClick={() => {
                                        setEmail({ emailAddress: "" });
                                        // Clear stored email when user changes it
                                        localStorage.removeItem('checkoutGuestEmail');
                                        setStep(CheckoutStep.Account);
                                        setReserveRequestResp(undefined);
                                        setPaymentVis("hidden");
                                    }}>
                                        Change
                                    </Button>
                                )}
                            </div>
                        )}
                    </Card.Body>
                </Card>
                <Card className="mb-2">
                    <CardHeader>
                        <CheckoutIndicator step={2} success={step > CheckoutStep.Reservation} /> Reservation Details
                    </CardHeader>
                    {step === CheckoutStep.Reservation && checkout && checkout.start && checkout.facility && (!validationRequestId ? true : validationRequestData) ? <Card.Body>
                        <ReservationDetailsHourly                                             
                            start={checkout.start}
                            end={checkout.end}
                            location={checkout.facility.name}
                            facilityId={checkout.facility.facilityId}
                            isCheckout={true}
                            onRateAvailable={handleRateAvailable}
                            onOptInFeesChange={handleOptInFeesChange}
                            editable={false}
                            validationRequest={validationRequestData}
                            initialParkNow={checkout.parkNow}
                            initialParkNowMode={checkout.parkNowMode}
                            extendMode={!!checkout.extendSession}
                            extendSession={checkout.extendSession}
                        />
                        {!isInitializing && !rateAvailable && (
                            <div className="alert alert-warning mt-2" role="alert">
                                <strong>Rate Unavailable:</strong> Parking is not available during the selected time period. Please return to search and try a different time.
                            </div>
                        )}
                        <div className="mt-2">
                            {checkout.vehicles.map( ( v, i ) => <VehicleCard
                                key={i}
                                vehicle={v}
                                deleteVehicle={ () => setCheckout({ ...checkout, vehicles: checkout.vehicles.filter((_, index) => index !== i) }) }
                                vehicles={checkout.vehicles} /> )}
                        </div>
                         {step === CheckoutStep.Reservation &&       
                         requireLicensePlate &&               
                         checkout.vehicles.length === 0 && !validationRequestId &&
                         <div className="my-4 d-flex gap-2">
                            {myVehicles.length > 0 && <Button className="w-100 mt-2" onClick={ () => setShowChooseVehicle( true ) }>
                                Add My Vehicle
                            </Button>}
                            <Button className="w-100 mt-2 btn-main very-opaque" onClick={ () => setShowAddVehicle( true ) }>
                                Add New Vehicle
                            </Button>
                        </div>
}
                        {step === CheckoutStep.Reservation
                        && <Button 
                            className="w-100 mt-2 btn-main" 
                            onClick={ () => setStep( CheckoutStep.Payment ) }
                            disabled={(requireLicensePlate && checkout.vehicles.length === 0 || isInitializing || !rateAvailable) && !validationRequestId}>
                            Continue to Payment
                        </Button>}
                    </Card.Body> : <span>
                    </span>}
                </Card>
                <Card>
                    <CardHeader>
                        <CheckoutIndicator step={3} success={step > CheckoutStep.Payment} /> Payment Details
                    </CardHeader>
                    {step === CheckoutStep.Payment && <Card.Body>
                        <Button variant="outline-secondary" size="sm" className="mb-3 px-3" onClick={() => {                                     
                            setStep(CheckoutStep.Reservation);
                            setPaymentVis("hidden");
                        }}><ArrowLeft className="me-1" style={{ marginTop: "-5px" }} />Back To Details</Button>
                        <div style={{ visibility: paymentVis }}>
                            {reserveRequestResp && options && <Elements stripe={stripePromise} options={options}>
                                <StripeHourlyCheckoutForm reservationDetails={reserveRequestResp} requestId={validationRequestId} />
                            </Elements>}
                        </div>
                    </Card.Body>}
                </Card>
            </Col>
        </Row>
    </Content>;
}